import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const tripleWhale: Competitor = {
  "id": "triple-whale",
  "name": "Triple Whale",
  "group": "ecomm",
  "website": "https://www.triplewhale.com",
  "owner": "Triple Whale (independent, venture-backed; no acquisition found)",
  "summary": "Shopify-first ecommerce analytics and attribution platform built around its own tracking pixel, prebuilt dashboards and the Moby AI agent layer. Aimed at DTC brands that mainly sell through Shopify and spend heavily on paid social and search.",
  "bestFor": [
    "Shopify DTC brands whose main question is paid-ad attribution and blended ROAS",
    "Teams without a data engineer who want dashboards, a pixel and benchmarks running in days instead of a multi-month build",
    "Brands that want AI agents to run reports, alerts and (with approval) ad-account changes"
  ],
  "notFor": [
    "Brands where wholesale, retail/POS, ERP or marketplace revenue is a large share of the business and must sit in one source of truth",
    "Companies that need finance-grade, audited revenue and margin definitions owned by their own team",
    "Non-Shopify storefronts (WooCommerce, BigCommerce, headless) that need deep support, since coverage there is thinner"
  ],
  "strengths": [
    "Own first-party pixel (Triple Pixel) with multi-touch attribution and post-purchase surveys out of the box; a custom warehouse build has to license or build attribution separately",
    "Prebuilt connectors for Shopify, Amazon, TikTok Shop and a long list of ad platforms (Meta, Google, TikTok, Snapchat, Pinterest, Microsoft and more), with no pipelines to maintain",
    "Industry and peer benchmarks (Automate plan), which a single brand's own warehouse cannot produce",
    "Sends first-party conversion data back to Meta, Google, TikTok and Klaviyo, which a reporting-only warehouse does not do without extra tooling",
    "Free tier and self-serve start, so the cost of trying it is far below a consultant-led Snowflake + dbt + BI project",
    "Moby AI agents can schedule reports and alerts and, with approval guardrails, act in connected ad platforms",
    "Higher tiers add marketing mix modeling, incrementality and GeoLift testing (Compass / Enterprise), which most custom builds do not include"
  ],
  "limitations": [
    "Shopify-first: a third-party review argues it cannot be the single source of truth for multi-channel brands because wholesale and marketplace revenue sit largely outside its core coverage (Eightx review, June 2026)",
    "No NetSuite, ERP, Faire or retail POS connector is listed on its integrations page; retail and wholesale data and custom integrations are positioned as Enterprise features",
    "TikTok Shop integration is US-only, excludes platform/commission/affiliate fees and customer-level metrics for now, and is left out of blended metrics by default",
    "Export to your own Snowflake or BigQuery is a paid Data Warehouse Sync add-on; syncs append rows, deduplication is the customer's job, and output schemas cannot be edited in place",
    "Plan prices scale with annual GMV and paid plans are 12-month subscriptions even when paid monthly (per third-party pricing review), so cost rises as the brand grows",
    "Attribution numbers are modeled estimates, not reconciled accounting revenue (third-party review)",
    "SSO and audit logs were still listed as coming soon on the plans help article"
  ],
  "pricing": {
    "model": "revenue-based (annual GMV bands) plus AI usage credits",
    "publicStartingPrice": "Free plan $0; Foundation from about $219/month (under $250K GMV) per a July 2026 third-party snapshot of Triple Whale's pricing; Triple Whale's own help center says prices vary by GMV and does not list them",
    "freeTier": "yes - Free plan with core integrations including Amazon, Triple Pixel, first/last-click attribution, Moby Chat, 12-month lookback",
    "notes": "Current plan names are Free, Foundation, Automate and Enterprise (help center). Third-party snapshot (July 2026): Foundation $219/mo under $250K GMV, $549/mo at $1M-$2.5M, $2,529/mo at $15M-$20M; Automate $749/mo under $250K, $1,349/mo at $1M-$2.5M, $4,199/mo at $15M-$20M; $20M+ is quote-only. Enterprise is custom and generally aimed at about $10M+ GMV. Annual prepay advertised as two months free; paid plans described as 12-month commitments. Add-ons: Data Warehouse Sync (contact sales), Conversion and Retention add-ons, Compass/Unified Measurement (MMM, incrementality), extra Moby usage. The live pricing page renders inconsistent legacy price blocks, so exact figures should be confirmed with a quote.",
    "sourceUrl": "https://kb.triplewhale.com/en/articles/16046642-understanding-triple-whale-plans-foundation-automate-and-enterprise",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "shopify": {
      "value": "yes",
      "note": "Core platform; Shopify-first",
      "sourceUrl": "https://triplewhale.com/integrations"
    },
    "amazon": {
      "value": "yes",
      "note": "Amazon NA, EU and FE listed; included in the Free plan's integrations",
      "sourceUrl": "https://triplewhale.com/integrations"
    },
    "tiktok_shop": {
      "value": "partial",
      "note": "Paid plans only, US stores only; platform/affiliate fees and customer-level metrics not yet included",
      "sourceUrl": "https://kb.triplewhale.com/en/articles/12884859-tiktok-shop-integration",
      "badge": "US only, paid plans"
    },
    "wholesale_retail_or_erp": {
      "value": "partial",
      "note": "ShipBob and ShipStation listed; no NetSuite/ERP/POS listed; retail and wholesale data offered on Enterprise",
      "sourceUrl": "https://kb.triplewhale.com/en/articles/16046642-understanding-triple-whale-plans-foundation-automate-and-enterprise",
      "badge": "Enterprise plan"
    },
    "ad_platforms": {
      "value": "yes",
      "note": "Meta, Google, TikTok plus Snapchat, Pinterest, Microsoft Ads, Criteo and others",
      "sourceUrl": "https://triplewhale.com/integrations"
    },
    "first_party_pixel_attribution": {
      "value": "yes",
      "note": "Triple Pixel with multi-touch attribution on Foundation and up; free plan has first/last click",
      "sourceUrl": "https://kb.triplewhale.com/en/articles/16046642-understanding-triple-whale-plans-foundation-automate-and-enterprise"
    },
    "mmm_incrementality": {
      "value": "yes",
      "note": "MMM, incrementality and GeoLift via Compass on Enterprise",
      "sourceUrl": "https://kb.triplewhale.com/en/articles/16046642-understanding-triple-whale-plans-foundation-automate-and-enterprise",
      "badge": "Enterprise plan"
    },
    "profit_cogs": {
      "value": "yes",
      "note": "Profitability analysis listed on pricing page; ShipBob adds fulfillment costs",
      "sourceUrl": "https://www.triplewhale.com/pricing"
    },
    "ltv_cohorts": {
      "value": "yes",
      "note": "Cohort analysis and 60/90-day LTV analysis listed",
      "sourceUrl": "https://www.triplewhale.com/pricing"
    },
    "subscription_analytics": {
      "value": "yes",
      "note": "Subscription tracking on Foundation; Recharge and Skio connectors",
      "sourceUrl": "https://kb.triplewhale.com/en/articles/16046642-understanding-triple-whale-plans-foundation-automate-and-enterprise"
    },
    "warehouse_access": {
      "value": "yes",
      "note": "Managed warehouse queryable with in-app SQL; export to your own Snowflake/BigQuery is a paid add-on",
      "sourceUrl": "https://triplewhale.readme.io/docs/sync-to-warehouse"
    },
    "custom_metrics": {
      "value": "yes",
      "note": "Custom metrics included in Foundation",
      "sourceUrl": "https://kb.triplewhale.com/en/articles/16046642-understanding-triple-whale-plans-foundation-automate-and-enterprise"
    },
    "custom_sql": {
      "value": "yes",
      "note": "SQL analysis inside Triple Whale on Foundation and up",
      "sourceUrl": "https://kb.triplewhale.com/en/articles/16046642-understanding-triple-whale-plans-foundation-automate-and-enterprise"
    },
    "ai_assistant": {
      "value": "yes",
      "note": "Moby chat, automations and approval-gated actions; read-only MCP server",
      "sourceUrl": "https://kb.triplewhale.com/en/articles/16046642-understanding-triple-whale-plans-foundation-automate-and-enterprise"
    },
    "mobile_app": {
      "value": "yes",
      "note": "Mobile app listed on pricing page, including free tier",
      "sourceUrl": "https://www.triplewhale.com/pricing"
    }
  },
  "recentChanges": [
    {
      "date": "2025-Q1",
      "change": "Moved from GMV-based annual tiers to monthly plans (Free/Starter/Advanced/Professional) per a pricing tracker; later replaced by the Foundation/Automate/Enterprise structure",
      "sourceUrl": "https://pricingsaas.com/companies/triplewhale"
    },
    {
      "date": "2026-02",
      "change": "Added monthly AI credit allowances to plans, then removed them from plan feature lists in March; Compass add-on renamed Unified Measurement (per pricing tracker)",
      "sourceUrl": "https://pricingsaas.com/companies/triplewhale"
    },
    {
      "date": "2026-05",
      "change": "Launched Moby 2, positioned as an AI operating system with an autopilot mode that can act across media buying, creative and site conversion",
      "sourceUrl": "https://www.pulse.bot/ecommerce/news/triple-whale-rolls-out-moby-2-ai-operating-system-for-60000-ecommerce-brands-bf236b27-a8a5-4b6b-96b1-7ffec5e194ac/"
    },
    {
      "date": "2026-06",
      "change": "Retention add-on price cut from $79 to $19/month and Conversion add-on raised from $19 to $79/month (per pricing tracker)",
      "sourceUrl": "https://pricingsaas.com/companies/triplewhale"
    },
    {
      "date": "2026",
      "change": "Plans restructured into Free, Foundation, Automate and Enterprise with GMV-based pricing and a read-only MCP server",
      "sourceUrl": "https://kb.triplewhale.com/en/articles/16046642-understanding-triple-whale-plans-foundation-automate-and-enterprise"
    }
  ],
  "sources": [
    {
      "title": "Triple Whale help: Understanding plans (Foundation, Automate, Enterprise)",
      "url": "https://kb.triplewhale.com/en/articles/16046642-understanding-triple-whale-plans-foundation-automate-and-enterprise"
    },
    {
      "title": "Triple Whale pricing page",
      "url": "https://www.triplewhale.com/pricing"
    },
    {
      "title": "Triple Whale integrations",
      "url": "https://triplewhale.com/integrations"
    },
    {
      "title": "Triple Whale docs: Sync to BigQuery/Snowflake",
      "url": "https://triplewhale.readme.io/docs/sync-to-warehouse"
    },
    {
      "title": "Triple Whale help: Snowflake integration",
      "url": "https://kb.triplewhale.com/en/articles/10067655-snowflake-integration"
    },
    {
      "title": "Triple Whale help: TikTok Shop integration",
      "url": "https://kb.triplewhale.com/en/articles/12884859-tiktok-shop-integration"
    },
    {
      "title": "Triple Whale data platform",
      "url": "https://www.triplewhale.com/data-platform"
    },
    {
      "title": "Venon: Triple Whale pricing 2026 (third-party, July 2026 snapshot)",
      "url": "https://venon.io/blog/triple-whale-pricing"
    },
    {
      "title": "PricingSaaS: Triple Whale pricing history (third-party)",
      "url": "https://pricingsaas.com/companies/triplewhale"
    },
    {
      "title": "Eightx: Triple Whale review (third-party, June 2026)",
      "url": "https://eightx.co/blog/compare/reviews/triple-whale-for-ecommerce-review"
    },
    {
      "title": "Pulse: Moby 2 launch (May 2026)",
      "url": "https://www.pulse.bot/ecommerce/news/triple-whale-rolls-out-moby-2-ai-operating-system-for-60000-ecommerce-brands-bf236b27-a8a5-4b6b-96b1-7ffec5e194ac/"
    }
  ],
  "lastReviewed": "2026-11-03"
};
